/**
 * `/campus` — public page for Bystep Campus: Builder & Problem Solver (docs/campus/*).
 * Landing-styled (`.mk`, light-only, no shadcn); the partnership form posts to `/api/public/campus-lead`.
 * Every claim here maps to a shipped mechanism (templates, client viewer, phase approval, readiness, proposal PDF, benchmark).
 */
import { useEffect, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Award,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  Compass,
  GraduationCap,
  Lightbulb,
  Loader2,
  Rocket,
  ShieldCheck,
  Trophy,
  Wrench,
} from 'lucide-react'
import {
  Container,
  FeatureCol,
  MkAnchor,
  MkFooter,
  MkLink,
  MkNav,
  Overline,
  SectionHead,
} from '#/components/landing/mk'
import { ApiError } from '#/lib/api'
import {
  CAMPUS_PARTNERSHIPS,
  CAMPUS_ROLES,
  CAMPUS_TRACKS,
  submitCampusLead,
} from '#/lib/api-campus'
import type {
  CampusPartnership,
  CampusRole,
  CampusTrack,
} from '#/lib/api-campus'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/campus')({
  head: () => ({
    meta: [
      { title: m.cp_meta_title() },
      { name: 'description', content: m.cp_meta_desc() },
      { property: 'og:title', content: m.cp_meta_title() },
      { property: 'og:description', content: m.cp_meta_desc() },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://bystep.dev/brand/bystep-og.png' },
    ],
  }),
  component: CampusPage,
})

const inputCls =
  'h-11 w-full rounded-lg border border-[var(--mk-border)] bg-white px-3 text-[15px] text-[var(--mk-fg)] outline-none placeholder:text-[var(--mk-fg-muted)] focus:border-[var(--mk-fg)]'
const labelCls = 'text-[13px] text-[var(--mk-fg-muted)]'

function CampusPage() {
  // Preselected partnership for the form; set by the Campus Lead / pricing CTAs (V2, PLAN_CAMPUS_V2 V4).
  const [preset, setPreset] = useState<CampusPartnership>('kelas')
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <Hero />
        <Journey />
        <Tracks />
        <Calendar />
        <Who />
        <Why />
        <CampusLead onApply={() => setPreset('lead')} />
        <Challenge />
        <Graduation />
        <Compare />
        <Pricing onPick={setPreset} />
        <Partnership preset={preset} />
        <Faq />
        <FinalCta />
      </main>
      <MkFooter />
    </div>
  )
}

// ---------------------------------------------------------------- hero
function Hero() {
  const facts = [
    { v: m.cp_fact_1_v(), l: m.cp_fact_1_l() },
    { v: m.cp_fact_2_v(), l: m.cp_fact_2_l() },
    { v: m.cp_fact_3_v(), l: m.cp_fact_3_l() },
    { v: m.cp_fact_4_v(), l: m.cp_fact_4_l() },
  ]
  return (
    <section className="pt-16 pb-12 lg:pt-24">
      <Container className="flex flex-col items-center gap-6 text-center">
        <Overline>{m.cp_hero_overline()}</Overline>
        <h1 className="max-w-[18ch]">{m.cp_hero_title()}</h1>
        <p className="mk-lead max-w-[60ch]">{m.cp_hero_sub()}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-3">
          <MkAnchor href="#kerjasama">{m.cp_hero_cta()}</MkAnchor>
          <MkLink
            to="/sign-in"
            search={{ redirect: '/plan?flow=new' }}
            variant="secondary"
            arrow
          >
            {m.cp_hero_cta2()}
          </MkLink>
        </div>
        <a
          href="#lead"
          className="text-[15px] font-medium text-[var(--mk-accent)] underline-offset-4 hover:underline"
        >
          {m.cp_hero_lead_link()}
        </a>
        <ul className="mt-6 grid w-full max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4">
          {facts.map((f) => (
            <li
              key={f.v}
              className="mk-card-white flex flex-col gap-1 p-4 text-left"
            >
              <span className="font-heading text-xl font-semibold tracking-[-0.02em]">
                {f.v}
              </span>
              <span className="text-[13px] text-[var(--mk-fg-muted)]">
                {f.l}
              </span>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 4-stage journey (V2: cheap entry, clear exit — BANDING §2.2)
function Journey() {
  const steps = [
    { t: m.cp_jr_1_t(), w: m.cp_jr_1_w(), d: m.cp_jr_1_d() },
    { t: m.cp_jr_2_t(), w: m.cp_jr_2_w(), d: m.cp_jr_2_d() },
    { t: m.cp_jr_3_t(), w: m.cp_jr_3_w(), d: m.cp_jr_3_d() },
    { t: m.cp_jr_4_t(), w: m.cp_jr_4_w(), d: m.cp_jr_4_d() },
  ]
  return (
    <section
      id="tahapan"
      className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-16 lg:py-24"
    >
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_jr_overline()}
          title={m.cp_jr_title()}
          lead={m.cp_jr_sub()}
        />
        <ol className="grid gap-3 md:grid-cols-4">
          {steps.map((st, i) => (
            <li
              key={st.t}
              className="mk-card-white relative flex flex-col gap-2 p-6"
            >
              <span className="font-heading text-4xl font-semibold tracking-[-0.03em] text-[var(--mk-accent)]">
                {i + 1}
              </span>
              <h3 className="text-lg!">{st.t}</h3>
              <span className="text-[12px] font-medium tracking-[0.02em] text-[var(--mk-fg-muted)] uppercase">
                {st.w}
              </span>
              <p className="text-[14px] leading-relaxed text-[var(--mk-fg-muted)]">
                {st.d}
              </p>
            </li>
          ))}
        </ol>
        <Link
          to="/design-room"
          className="mx-auto flex w-fit max-w-full items-start gap-3 rounded-lg border border-dashed border-[var(--mk-border)] bg-white px-4 py-3 text-left transition-colors hover:border-[var(--mk-fg)]"
        >
          <Compass className="mt-0.5 size-4 shrink-0 text-[var(--mk-accent)]" aria-hidden />
          <span>
            <strong className="text-[14px] font-medium text-[var(--mk-fg)]">{m.dr_campus_callout_title()}</strong>
            <span className="mt-0.5 block text-[13px] leading-relaxed text-[var(--mk-fg-muted)]">{m.dr_campus_callout_desc()}</span>
            <span className="mt-1.5 block text-[13px] font-medium text-[var(--mk-accent)]">{m.dr_campus_callout_link()} →</span>
          </span>
        </Link>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- two tracks
function Tracks() {
  const tracks = [
    {
      icon: Rocket,
      title: m.cp_builder_title(),
      tag: m.cp_builder_tag(),
      rows: [
        [m.cp_track_for(), m.cp_builder_for()],
        [m.cp_track_start(), m.cp_builder_start()],
        [m.cp_track_end(), m.cp_builder_end()],
      ],
    },
    {
      icon: Wrench,
      title: m.cp_solver_title(),
      tag: m.cp_solver_tag(),
      rows: [
        [m.cp_track_for(), m.cp_solver_for()],
        [m.cp_track_start(), m.cp_solver_start()],
        [m.cp_track_end(), m.cp_solver_end()],
      ],
    },
  ]
  return (
    <section id="jalur" className="scroll-mt-20 py-16 lg:py-24">
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_tracks_overline()}
          title={m.cp_tracks_title()}
          lead={m.cp_tracks_sub()}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {tracks.map(({ icon: I, title, tag, rows }) => (
            <article
              key={title}
              className="mk-card-white flex flex-col gap-6 p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-[var(--mk-dark)] text-white">
                  <I className="size-5" aria-hidden />
                </span>
                <div>
                  <h3 className="text-xl!">{title}</h3>
                  <p className="text-[13px] font-medium tracking-[0.02em] text-[var(--mk-accent)] uppercase">
                    {tag}
                  </p>
                </div>
              </div>
              <dl className="flex flex-col gap-4">
                {rows.map(([k, v]) => (
                  <div
                    key={k}
                    className="grid gap-1 sm:grid-cols-[7rem_1fr] sm:gap-4"
                  >
                    <dt className="text-[13px] font-medium text-[var(--mk-fg-muted)]">
                      {k}
                    </dt>
                    <dd className="text-[15px] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 14-week calendar
function Calendar() {
  const steps = [
    { w: m.cp_cal_1_w(), t: m.cp_cal_1_t(), d: m.cp_cal_1_d() },
    { w: m.cp_cal_2_w(), t: m.cp_cal_2_t(), d: m.cp_cal_2_d() },
    { w: m.cp_cal_3_w(), t: m.cp_cal_3_t(), d: m.cp_cal_3_d() },
    { w: m.cp_cal_4_w(), t: m.cp_cal_4_t(), d: m.cp_cal_4_d() },
    { w: m.cp_cal_5_w(), t: m.cp_cal_5_t(), d: m.cp_cal_5_d() },
    { w: m.cp_cal_6_w(), t: m.cp_cal_6_t(), d: m.cp_cal_6_d() },
    { w: m.cp_cal_7_w(), t: m.cp_cal_7_t(), d: m.cp_cal_7_d() },
  ]
  return (
    <section
      id="kalender"
      className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-16 lg:py-24"
    >
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_cal_overline()}
          title={m.cp_cal_title()}
          lead={m.cp_cal_sub()}
        />
        <ol className="relative grid gap-3 md:grid-cols-7">
          {steps.map((s, i) => (
            <li
              key={s.w}
              className="mk-card-white relative flex flex-col gap-2 p-5"
            >
              <span className="inline-flex size-7 items-center justify-center rounded-full bg-[var(--mk-dark)] text-[13px] font-semibold text-white">
                {i + 1}
              </span>
              <span className="text-[12px] font-medium tracking-[0.02em] text-[var(--mk-accent)] uppercase">
                {s.w}
              </span>
              <h3 className="text-base!">{s.t}</h3>
              <p className="text-[13px] leading-relaxed text-[var(--mk-fg-muted)]">
                {s.d}
              </p>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- who it is for
function Who() {
  return (
    <section className="py-16 lg:py-24">
      <Container className="flex flex-col gap-12">
        <SectionHead overline={m.cp_who_overline()} title={m.cp_who_title()} />
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <FeatureCol title={m.cp_who_1_t()}>{m.cp_who_1_d()}</FeatureCol>
          <FeatureCol title={m.cp_who_2_t()}>{m.cp_who_2_d()}</FeatureCol>
          <FeatureCol title={m.cp_who_3_t()}>{m.cp_who_3_d()}</FeatureCol>
          <FeatureCol title={m.cp_who_4_t()}>{m.cp_who_4_d()}</FeatureCol>
        </div>
        <p className="mx-auto flex max-w-3xl items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[15px] text-emerald-900">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{m.cp_who_free()}</span>
        </p>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- why lecturers can trust it (dark) + lecturer view mock
function Why() {
  const points = [
    {
      icon: ShieldCheck,
      t: m.cp_why_1_t(),
      d: m.cp_why_1_d(),
      s: m.cp_why_1_src(),
    },
    {
      icon: Lightbulb,
      t: m.cp_why_2_t(),
      d: m.cp_why_2_d(),
      s: m.cp_why_2_src(),
    },
    {
      icon: Briefcase,
      t: m.cp_why_3_t(),
      d: m.cp_why_3_d(),
      s: m.cp_why_3_src(),
    },
  ]
  return (
    <section id="bukti" className="mk-dark-section scroll-mt-20 py-24 lg:py-32">
      <Container className="flex flex-col gap-12">
        <SectionHead
          align="left"
          overline={
            <span className="text-[#7d9bff]">{m.cp_why_overline()}</span>
          }
          title={m.cp_why_title()}
          lead={m.cp_why_sub()}
        />
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          <ul className="flex flex-col gap-8">
            {points.map(({ icon: I, t, d, s }) => (
              <li key={t} className="flex gap-4">
                <span className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-lg border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)]">
                  <I className="size-4 text-[#7d9bff]" aria-hidden />
                </span>
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-lg!">{t}</h3>
                  <p className="leading-relaxed text-[var(--mk-dark-fg-muted)]">
                    {d}
                  </p>
                  <p className="text-[13px] text-[var(--mk-dark-fg-muted)]/80 italic">
                    {s}
                  </p>
                </div>
              </li>
            ))}
            <li>
              <a
                href="/docs"
                className="text-[15px] font-medium text-white underline-offset-4 hover:underline"
              >
                {m.cp_why_link()}
              </a>
            </li>
          </ul>
          <LecturerMock />
        </div>
      </Container>
    </section>
  )
}

/** Static preview of the lecturer's `/client/:planId` view — plain Tailwind, no live data. */
function LecturerMock() {
  const bars = [
    { name: m.cp_mock_f1(), verified: 100, override: 0 },
    { name: m.cp_mock_f2(), verified: 75, override: 8 },
    { name: m.cp_mock_f3(), verified: 30, override: 0 },
  ]
  return (
    <div className="mk-card-white overflow-hidden p-0 text-[var(--mk-fg)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mk-border)] px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="inline-block size-7 rounded-md bg-[#2757e6]" />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-[#2757e6]">
              {m.cp_mock_ws()}
            </div>
            <div className="text-[11px] text-[var(--mk-fg-muted)]">
              {m.cp_mock_plan()}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-4 bg-[var(--mk-bg-muted)] p-5">
        <div className="rounded-xl border-2 border-[#2757e6] bg-white p-4">
          <div className="text-[15px] font-semibold">{m.cp_mock_pending()}</div>
          <div className="mt-3 flex gap-2">
            <div className="h-9 flex-1 truncate rounded-lg border border-[var(--mk-border)] bg-white px-3 text-[13px] leading-9 text-[var(--mk-fg-muted)]">
              {m.cp_mock_note()}
            </div>
            <div className="inline-flex h-9 items-center rounded-lg bg-[#2757e6] px-4 text-[13px] font-medium whitespace-nowrap text-white">
              {m.cp_mock_approve()}
            </div>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { l: m.cp_mock_verified(), v: '18/22', c: '#16a34a' },
            { l: m.cp_mock_evidence(), v: '89%', c: undefined },
            { l: m.cp_mock_override(), v: '1', c: '#d97706' },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-xl border border-[var(--mk-border)] bg-white p-3"
            >
              <div className="text-[11px] text-[var(--mk-fg-muted)]">{s.l}</div>
              <div
                className="text-xl font-semibold tabular-nums"
                style={s.c ? { color: s.c } : undefined}
              >
                {s.v}
              </div>
            </div>
          ))}
        </div>
        <ul className="space-y-3 rounded-xl border border-[var(--mk-border)] bg-white p-4">
          {bars.map((b) => (
            <li key={b.name}>
              <div className="flex items-baseline justify-between text-[13px]">
                <span>{b.name}</span>
                <span className="text-[11px] text-[var(--mk-fg-muted)]">
                  {b.verified}%
                </span>
              </div>
              <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-[#eef1f6]">
                <span
                  style={{ width: `${b.verified}%`, background: '#16a34a' }}
                />
                <span
                  style={{ width: `${b.override}%`, background: '#d97706' }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- Campus Lead (V2: student-led track — BANDING §2.1)
function CampusLead({ onApply }: { onApply: () => void }) {
  const get = [
    m.cp_lead_get_1(),
    m.cp_lead_get_2(),
    m.cp_lead_get_3(),
    m.cp_lead_get_4(),
    m.cp_lead_get_5(),
  ]
  const give = [m.cp_lead_give_1(), m.cp_lead_give_2(), m.cp_lead_give_3()]
  const tiers = [
    { n: m.cp_tier_1_n(), d: m.cp_tier_1_d() },
    { n: m.cp_tier_2_n(), d: m.cp_tier_2_d() },
    { n: m.cp_tier_3_n(), d: m.cp_tier_3_d() },
  ]
  return (
    <section id="lead" className="scroll-mt-20 py-24 lg:py-32">
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_lead_overline()}
          title={m.cp_lead_title()}
          lead={m.cp_lead_sub()}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {[
            { t: m.cp_lead_get_t(), items: get },
            { t: m.cp_lead_give_t(), items: give },
          ].map((col) => (
            <div key={col.t} className="mk-card-white p-7">
              <h3 className="text-lg!">{col.t}</h3>
              <ul className="mt-4 flex flex-col gap-2.5">
                {col.items.map((it) => (
                  <li
                    key={it}
                    className="flex gap-2.5 text-[15px] leading-relaxed"
                  >
                    <CheckCircle2
                      className="mt-1 size-4 shrink-0 text-emerald-600"
                      aria-hidden
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <ol className="grid gap-3 md:grid-cols-3">
          {tiers.map((t, i) => (
            <li
              key={t.n}
              className={cn(
                'flex flex-col gap-2 rounded-[var(--mk-radius)] border p-6',
                i === 2
                  ? 'border-transparent bg-[var(--mk-dark)] text-white'
                  : 'mk-card-white',
              )}
            >
              <span className="flex items-center gap-2">
                <GraduationCap
                  className={cn(
                    'size-5',
                    i === 2 ? 'text-[#7d9bff]' : 'text-[var(--mk-accent)]',
                  )}
                  aria-hidden
                />
                <span className="text-[12px] font-medium tracking-[0.04em] uppercase">
                  Tier {i + 1}
                </span>
              </span>
              <h3 className="text-xl!">{t.n}</h3>
              <p
                className={cn(
                  'text-[14px] leading-relaxed',
                  i === 2
                    ? 'text-[var(--mk-dark-fg-muted)]'
                    : 'text-[var(--mk-fg-muted)]',
                )}
              >
                {t.d}
              </p>
            </li>
          ))}
        </ol>
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="mk-muted max-w-[70ch] text-[14px]">{m.cp_lead_req()}</p>
          <MkAnchor href="#kerjasama" onClick={onApply}>
            {m.cp_lead_cta()}
          </MkAnchor>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- Build Challenge (V2: branded competition — BANDING §2.4)
function Challenge() {
  const crit = [
    { v: m.cp_ch_1_v(), t: m.cp_ch_1_t(), d: m.cp_ch_1_d() },
    { v: m.cp_ch_2_v(), t: m.cp_ch_2_t(), d: m.cp_ch_2_d() },
    { v: m.cp_ch_3_v(), t: m.cp_ch_3_t(), d: m.cp_ch_3_d() },
  ]
  const info = [
    { t: m.cp_ch_entry_t(), d: m.cp_ch_entry_d() },
    { t: m.cp_ch_prize_t(), d: m.cp_ch_prize_d() },
    { t: m.cp_ch_when_t(), d: m.cp_ch_when_d() },
  ]
  return (
    <section
      id="challenge"
      className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-16 lg:py-24"
    >
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_ch_overline()}
          title={m.cp_ch_title()}
          lead={m.cp_ch_sub()}
        />
        <ul className="grid gap-3 md:grid-cols-3">
          {crit.map((c) => (
            <li key={c.t} className="mk-card-white flex flex-col gap-1 p-6">
              <span className="font-heading text-4xl font-semibold tracking-[-0.03em]">
                {c.v}
                <span className="ml-1 text-base font-normal text-[var(--mk-fg-muted)]">
                  / 100
                </span>
              </span>
              <h3 className="text-lg!">{c.t}</h3>
              <p className="text-[14px] leading-relaxed text-[var(--mk-fg-muted)]">
                {c.d}
              </p>
            </li>
          ))}
        </ul>
        <div className="grid gap-10 sm:grid-cols-3">
          {info.map((x) => (
            <FeatureCol key={x.t} title={x.t}>
              {x.d}
            </FeatureCol>
          ))}
        </div>
        <div className="mk-card-white flex flex-col gap-3 p-7 md:flex-row md:items-start md:gap-6">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--mk-dark)] text-white">
            <Award className="size-5" aria-hidden />
          </span>
          <div className="flex flex-col gap-1.5">
            <h3 className="text-lg!">{m.cp_cert_t()}</h3>
            <p className="text-[15px] leading-relaxed text-[var(--mk-fg-muted)]">
              {m.cp_cert_d()}
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- graduation gate (Phase 3 — PRD F3.5)
/** "Syarat lulus = aplikasi terbit": copy only, the campus_lead logic is untouched. */
function Graduation() {
  const items = [m.gd_campus_1(), m.gd_campus_2(), m.gd_campus_3()]
  return (
    <section id="lulus" className="scroll-mt-20 py-16 lg:py-24">
      <Container className="flex flex-col gap-8">
        <div className="mk-card-white flex flex-col gap-6 p-8 md:flex-row md:gap-10">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--mk-dark)] text-white">
            <Rocket className="size-5" aria-hidden />
          </span>
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl!">{m.gd_campus_title()}</h2>
            <p className="max-w-[62ch] text-[15px] leading-relaxed text-[var(--mk-fg-muted)]">
              {m.gd_campus_desc()}
            </p>
            <ul className="mt-1 flex flex-col gap-2.5">
              {items.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[15px] leading-relaxed">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[var(--mk-accent)]" aria-hidden />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- comparison: use together, not instead (V2 — BANDING §1)
function Compare() {
  const rows = [
    [m.cp_cmp_1_p(), m.cp_cmp_1_t(), m.cp_cmp_1_w()],
    [m.cp_cmp_2_p(), m.cp_cmp_2_t(), m.cp_cmp_2_w()],
    [m.cp_cmp_3_p(), m.cp_cmp_3_t(), m.cp_cmp_3_w()],
    [m.cp_cmp_4_p(), m.cp_cmp_4_t(), m.cp_cmp_4_w()],
    [m.cp_cmp_5_p(), m.cp_cmp_5_t(), m.cp_cmp_5_w()],
  ]
  return (
    <section id="banding" className="scroll-mt-20 py-16 lg:py-24">
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_cmp_overline()}
          title={m.cp_cmp_title()}
          lead={m.cp_cmp_sub()}
        />
        <div className="overflow-x-auto rounded-[var(--mk-radius)] border border-[var(--mk-border)] bg-white">
          <table className="w-full min-w-[720px] border-collapse text-left text-[14px]">
            <thead>
              <tr className="border-b border-[var(--mk-border)] bg-[var(--mk-bg-muted)] text-[12px] font-medium tracking-[0.04em] text-[var(--mk-fg-muted)] uppercase">
                <th className="px-5 py-3">{m.cp_cmp_h_prog()}</th>
                <th className="px-5 py-3">{m.cp_cmp_h_they()}</th>
                <th className="px-5 py-3">{m.cp_cmp_h_we()}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([p, t, w]) => (
                <tr
                  key={p}
                  className="border-b border-[var(--mk-border)] align-top last:border-b-0"
                >
                  <td className="px-5 py-4 font-medium">{p}</td>
                  <td className="px-5 py-4 leading-relaxed text-[var(--mk-fg-muted)]">
                    {t}
                  </td>
                  <td className="px-5 py-4 leading-relaxed">
                    <span className="flex gap-2">
                      <Trophy
                        className="mt-0.5 size-4 shrink-0 text-[var(--mk-accent)]"
                        aria-hidden
                      />
                      <span>{w}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- pricing (per team per semester)
type PriceCard = {
  name: string
  price: string
  per: string
  extra?: string
  desc: string
  cta: string
  href?: string
  to?: '/sign-in'
  featured?: boolean
  pick?: CampusPartnership
}
function Pricing({ onPick }: { onPick: (p: CampusPartnership) => void }) {
  const cards: PriceCard[] = [
    {
      name: m.cp_p_tim_name(),
      price: m.cp_p_tim_price(),
      per: m.cp_per_team_sem(),
      desc: m.cp_p_tim_desc(),
      cta: m.cp_p_tim_cta(),
      to: '/sign-in',
      featured: true,
    },
    {
      name: m.cp_p_kelas_name(),
      price: m.cp_p_kelas_price(),
      per: m.cp_per_team_sem(),
      desc: m.cp_p_kelas_desc(),
      cta: m.cp_p_kelas_cta(),
      href: '#kerjasama',
      pick: 'kelas',
    },
    {
      name: m.cp_p_mitra_name(),
      price: m.cp_p_mitra_price(),
      per: m.cp_per_team_sem(),
      extra: m.cp_p_mitra_extra(),
      desc: m.cp_p_mitra_desc(),
      cta: m.cp_p_mitra_cta(),
      href: '#kerjasama',
      pick: 'kampus',
    },
    {
      name: m.cp_p_solo_name(),
      price: m.cp_p_solo_price(),
      per: m.cp_per_month(),
      desc: m.cp_p_solo_desc(),
      cta: m.cp_p_solo_cta(),
      href: '#kerjasama',
      pick: 'perorangan',
    },
    {
      name: m.cp_p_event_name(),
      price: m.cp_p_event_price(),
      per: '',
      desc: m.cp_p_event_desc(),
      cta: m.cp_p_event_cta(),
      href: '#kerjasama',
      pick: 'komunitas',
    },
    {
      name: m.cp_p_lead_name(),
      price: m.cp_p_lead_price(),
      per: '',
      desc: m.cp_p_lead_desc(),
      cta: m.cp_p_lead_cta(),
      href: '#kerjasama',
      pick: 'lead',
    },
  ]
  return (
    <section id="harga" className="scroll-mt-20 py-24 lg:py-32">
      <Container>
        <SectionHead
          overline={m.cp_price_overline()}
          title={m.cp_price_title()}
          lead={m.cp_price_sub()}
        />
        <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <li
              key={c.name}
              className={cn(
                'flex flex-col gap-3 rounded-[var(--mk-radius)] border p-6',
                c.featured
                  ? 'border-transparent bg-[var(--mk-dark)] text-white'
                  : 'mk-card-white',
              )}
            >
              <span
                className={cn(
                  'text-sm font-medium tracking-[0.02em] uppercase',
                  c.featured ? 'text-[#7d9bff]' : 'text-[var(--mk-accent)]',
                )}
              >
                {c.name}
              </span>
              <p className="font-heading text-3xl font-semibold tracking-[-0.03em]">
                {c.price}
                {c.per ? (
                  <span
                    className={cn(
                      'ml-1 text-base font-normal',
                      c.featured
                        ? 'text-[var(--mk-dark-fg-muted)]'
                        : 'text-[var(--mk-fg-muted)]',
                    )}
                  >
                    {c.per}
                  </span>
                ) : null}
              </p>
              {c.extra ? (
                <p className="-mt-2 text-[13px] font-medium">{c.extra}</p>
              ) : null}
              <p
                className={cn(
                  'text-sm leading-relaxed',
                  c.featured
                    ? 'text-[var(--mk-dark-fg-muted)]'
                    : 'text-[var(--mk-fg-muted)]',
                )}
              >
                {c.desc}
              </p>
              <div className="mt-auto pt-3">
                {c.to ? (
                  <MkLink
                    to={c.to}
                    search={{ redirect: '/pricing' }}
                    className={cn(
                      'h-10 w-full',
                      c.featured &&
                        'bg-white text-[var(--mk-btn)] hover:bg-white/90',
                    )}
                  >
                    {c.cta}
                  </MkLink>
                ) : (
                  <MkAnchor
                    href={c.href}
                    variant="secondary"
                    className="h-10 w-full"
                    onClick={
                      c.pick
                        ? () => onPick(c.pick as CampusPartnership)
                        : undefined
                    }
                  >
                    {c.cta}
                  </MkAnchor>
                )}
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-center text-sm text-[var(--mk-fg-muted)]">
          {m.cp_price_note()}
        </p>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- partnership: give / ask + form
function Partnership({ preset }: { preset: CampusPartnership }) {
  const give = [
    m.cp_give_1(),
    m.cp_give_2(),
    m.cp_give_3(),
    m.cp_give_4(),
    m.cp_give_5(),
  ]
  const ask = [
    m.cp_ask_1(),
    m.cp_ask_2(),
    m.cp_ask_3(),
    m.cp_ask_4(),
    m.cp_ask_5(),
  ]
  return (
    <section
      id="kerjasama"
      className="scroll-mt-20 bg-[var(--mk-bg-muted)] py-16 lg:py-24"
    >
      <Container className="flex flex-col gap-12">
        <SectionHead
          overline={m.cp_partner_overline()}
          title={m.cp_partner_title()}
          lead={m.cp_partner_sub()}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {[
            { t: m.cp_give_title(), items: give },
            { t: m.cp_ask_title(), items: ask },
          ].map((col) => (
            <div key={col.t} className="mk-card-white p-7">
              <h3 className="text-lg!">{col.t}</h3>
              <ul className="mt-4 flex flex-col gap-2.5">
                {col.items.map((it) => (
                  <li
                    key={it}
                    className="flex gap-2.5 text-[15px] leading-relaxed"
                  >
                    <CheckCircle2
                      className="mt-1 size-4 shrink-0 text-emerald-600"
                      aria-hidden
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-2 text-center">
          <h3 className="text-2xl!">{m.cp_form_title()}</h3>
          <p className="mk-muted">{m.cp_form_desc()}</p>
        </div>
        <LeadForm preset={preset} />
      </Container>
    </section>
  )
}

const ROLE_LABEL: Record<CampusRole, () => string> = {
  dosen: () => m.cp_role_dosen(),
  mahasiswa: () => m.cp_role_mahasiswa(),
  himpunan: () => m.cp_role_himpunan(),
  inkubator: () => m.cp_role_inkubator(),
  mentor: () => m.cp_role_mentor(),
  lainnya: () => m.cp_role_lainnya(),
}
const PT_LABEL: Record<CampusPartnership, () => string> = {
  kelas: () => m.cp_pt_kelas(),
  komunitas: () => m.cp_pt_komunitas(),
  kampus: () => m.cp_pt_kampus(),
  perorangan: () => m.cp_pt_perorangan(),
  lead: () => m.cp_pt_lead(),
}
const TR_LABEL: Record<CampusTrack, () => string> = {
  builder: () => m.cp_tr_builder(),
  solver: () => m.cp_tr_solver(),
  keduanya: () => m.cp_tr_keduanya(),
}

function LeadForm({ preset }: { preset: CampusPartnership }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [institution, setInstitution] = useState('')
  const [role, setRole] = useState<CampusRole>('dosen')
  const [partnership, setPartnership] = useState<CampusPartnership>(preset)
  useEffect(() => {
    setPartnership(preset)
    if (preset === 'lead') setRole('mahasiswa')
  }, [preset])
  const [track, setTrack] = useState<CampusTrack>('keduanya')
  const [teams, setTeams] = useState('')
  const [startTerm, setStartTerm] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()))
      return setError(m.cp_form_err_email())
    if (institution.trim().length < 2) return setError(m.cp_form_err_inst())
    setBusy(true)
    try {
      const n = Number(teams)
      await submitCampusLead({
        email: email.trim(),
        institution: institution.trim(),
        role,
        partnership,
        track,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(Number.isFinite(n) && teams.trim()
          ? { teams: Math.max(0, Math.round(n)) }
          : {}),
        ...(startTerm.trim() ? { startTerm: startTerm.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        locale: getLocale() === 'id' ? 'id' : 'en',
      })
      setDone(true)
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined
      setError(
        code === 'rate_limited'
          ? m.cp_form_err_rate()
          : code === 'bad_request'
            ? m.cp_form_err_email()
            : m.cp_form_err_generic(),
      )
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="mk-card-white mx-auto flex w-full max-w-2xl flex-col items-center gap-2 p-8 text-center">
        <CheckCircle2 className="size-7 text-emerald-600" aria-hidden />
        <h3>{m.cp_form_ok_title()}</h3>
        <p className="mk-muted">{m.cp_form_ok_desc()}</p>
      </div>
    )
  }

  const Field = ({
    id,
    label,
    children,
    full,
  }: {
    id: string
    label: string
    children: React.ReactNode
    full?: boolean
  }) => (
    <div className={cn('flex flex-col gap-1.5', full && 'sm:col-span-2')}>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
    </div>
  )

  return (
    <form
      onSubmit={submit}
      className="mk-card-white mx-auto grid w-full max-w-2xl gap-4 p-7 sm:grid-cols-2"
    >
      <Field id="cp-email" label={m.cp_form_email()}>
        <input
          id="cp-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          placeholder="nama@kampus.ac.id"
          autoComplete="email"
        />
      </Field>
      <Field id="cp-name" label={m.cp_form_name()}>
        <input
          id="cp-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
          autoComplete="name"
          maxLength={120}
        />
      </Field>
      <Field id="cp-inst" label={m.cp_form_institution()} full>
        <input
          id="cp-inst"
          required
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          className={inputCls}
          maxLength={160}
          autoComplete="organization"
        />
      </Field>
      <Field id="cp-role" label={m.cp_form_role()}>
        <select
          id="cp-role"
          value={role}
          onChange={(e) => setRole(e.target.value as CampusRole)}
          className={inputCls}
        >
          {CAMPUS_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]()}
            </option>
          ))}
        </select>
      </Field>
      <Field id="cp-pt" label={m.cp_form_partnership()}>
        <select
          id="cp-pt"
          value={partnership}
          onChange={(e) => setPartnership(e.target.value as CampusPartnership)}
          className={inputCls}
        >
          {CAMPUS_PARTNERSHIPS.map((p) => (
            <option key={p} value={p}>
              {PT_LABEL[p]()}
            </option>
          ))}
        </select>
      </Field>
      <Field id="cp-track" label={m.cp_form_track()}>
        <select
          id="cp-track"
          value={track}
          onChange={(e) => setTrack(e.target.value as CampusTrack)}
          className={inputCls}
        >
          {CAMPUS_TRACKS.map((t) => (
            <option key={t} value={t}>
              {TR_LABEL[t]()}
            </option>
          ))}
        </select>
      </Field>
      <Field id="cp-teams" label={m.cp_form_teams()}>
        <input
          id="cp-teams"
          type="number"
          min={0}
          max={5000}
          inputMode="numeric"
          value={teams}
          onChange={(e) => setTeams(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field id="cp-start" label={m.cp_form_start()}>
        <input
          id="cp-start"
          value={startTerm}
          onChange={(e) => setStartTerm(e.target.value)}
          className={inputCls}
          placeholder={m.cp_form_start_ph()}
          maxLength={60}
        />
      </Field>
      <Field id="cp-phone" label={m.cp_form_phone()}>
        <input
          id="cp-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputCls}
          maxLength={40}
          autoComplete="tel"
        />
      </Field>
      <Field id="cp-note" label={m.cp_form_note()} full>
        <textarea
          id="cp-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={1500}
          className="w-full rounded-lg border border-[var(--mk-border)] bg-white p-3 text-[15px] text-[var(--mk-fg)] outline-none focus:border-[var(--mk-fg)]"
        />
      </Field>
      {error ? (
        <p className="text-[15px] text-red-600 sm:col-span-2">{error}</p>
      ) : null}
      <div className="sm:col-span-2">
        <button
          type="submit"
          className="mk-btn mk-btn-primary h-12"
          disabled={busy}
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : null}
          {busy ? m.cp_form_sending() : m.cp_form_submit()}
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------- FAQ
function Faq() {
  const qa = [
    [m.cp_faq_1_q(), m.cp_faq_1_a()],
    [m.cp_faq_2_q(), m.cp_faq_2_a()],
    [m.cp_faq_3_q(), m.cp_faq_3_a()],
    [m.cp_faq_4_q(), m.cp_faq_4_a()],
    [m.cp_faq_5_q(), m.cp_faq_5_a()],
    [m.cp_faq_6_q(), m.cp_faq_6_a()],
    [m.cp_faq_7_q(), m.cp_faq_7_a()],
  ]
  return (
    <section id="faq" className="scroll-mt-20 py-16 lg:py-24">
      <Container className="flex flex-col gap-10">
        <SectionHead overline={m.cp_faq_overline()} title={m.cp_faq_title()} />
        <div className="mx-auto w-full max-w-3xl divide-y divide-[var(--mk-border)] border-y border-[var(--mk-border)]">
          {qa.map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[17px] font-medium">
                {q}
                <ChevronDown
                  className="size-4 shrink-0 text-[var(--mk-fg-muted)] transition-transform group-open:rotate-180"
                  aria-hidden
                />
              </summary>
              <p className="mk-muted mt-3 max-w-[70ch] leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- final CTA
function FinalCta() {
  return (
    <section className="mk-dark-section py-24">
      <Container className="flex flex-col items-center gap-5 text-center">
        <h2 className="max-w-[20ch]">{m.cp_final_title()}</h2>
        <p className="mk-lead max-w-[50ch] text-[var(--mk-dark-fg-muted)]">
          {m.cp_final_sub()}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-3">
          <MkAnchor href="#kerjasama">{m.cp_hero_cta()}</MkAnchor>
          <MkLink
            to="/sign-in"
            search={{ redirect: '/plan?flow=new' }}
            variant="secondary"
            arrow
          >
            {m.cp_hero_cta2()}
          </MkLink>
        </div>
      </Container>
    </section>
  )
}
